/**
 * Task #1416 — Hook condiviso per la sezione "Pipeline Health" della
 * dashboard RAG Analytics. Polling 30s, scope opzionale per `agent_id`
 * (variante compact riusata nel modal `RAGAnalyticsDashboard`).
 *
 * Il backend risponde sempre 200 anche con widget degradati: il flag
 * `degraded` per widget e l'array `anomalies` aggregato sono parte
 * integrante del payload (`Promise.allSettled` lato server).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../lib/api';
import type { RagPipelineHealthResponse } from '@easyflow/shared';

const POLL_INTERVAL_MS = 30_000;

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: string;
}

export interface UseRagPipelineHealthResult {
  data: RagPipelineHealthResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  lastFetchedAt: Date | null;
}

export function useRagPipelineHealth(opts: { agentId?: string | null } = {}): UseRagPipelineHealthResult {
  const { agentId } = opts;
  const [data, setData] = useState<RagPipelineHealthResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const cancelledRef = useRef<boolean>(false);

  const fetchOnce = useCallback(async () => {
    try {
      const qs = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
      const res = await apiClient.get<RagPipelineHealthResponse>(`/rag-analytics/pipeline-health${qs}`);
      const env = res as unknown as ApiEnvelope<RagPipelineHealthResponse>;
      if (cancelledRef.current) return;
      if (env.data) {
        setData(env.data);
        setError(null);
        setLastFetchedAt(new Date());
      } else if (env.error) {
        setError(env.error);
      }
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
  }, [fetchOnce]);

  return { data, loading, error, refetch: fetchOnce, lastFetchedAt };
}
